import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import EventsHero from './components/EventsHero';
import EventsFilters from './components/EventsFilters';
import EventsGrid from './components/EventsGrid';
import SortControls from '../discover/components/SortControls';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Event {
  id: string;
  title: string;
  description: string | null;
  category: string;
  start_date: string;
  end_date: string | null;
  location_type: string;
  location_city: string | null;
  location_country: string | null;
  cover_image: string | null;
  price: number | null;
  currency: string | null;
  is_published: boolean;
  registrations?: { count: number }[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [priceRange, setPriceRange] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [registeredEventIds, setRegisteredEventIds] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const PAGE_TITLE = 'Browse All Events – Find Events Near You | EVENTSHOWCASE';
    const PAGE_DESC = 'Browse all upcoming events on EVENTSHOWCASE. Filter by category, location, date, and price. Find free and paid events near you or online.';
    const PAGE_URL = `${SITE_URL}/events`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/7c2b9910beb1ba60e313f8f4af658b42.jpg';

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="keywords"]', 'content', 'browse events, upcoming events, events near me, free events, paid events, event discovery');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = PAGE_URL;

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Browse All Events',
      description: 'Browse all upcoming events on EVENTSHOWCASE. Filter by category, location, date, and price.',
      url: PAGE_URL,
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Events', item: PAGE_URL },
        ],
      },
    };

    const existing = document.getElementById('schema-events');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-events';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    fetchEvents();

    return () => {
      const el = document.getElementById('schema-events');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  useEffect(() => {
    if (user) fetchUserRegistrations();
    else setRegisteredEventIds(new Set());
  }, [user]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*, registrations(count)')
        .eq('is_published', true)
        .gte('start_date', new Date().toISOString())
        .order('start_date', { ascending: true });
      if (error) throw error;
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRegistrations = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('event_id')
        .eq('user_id', user.id);
      if (error) throw error;
      setRegisteredEventIds(new Set((data || []).map((r: any) => r.event_id)));
    } catch (err) {
      console.error('Error fetching registrations:', err);
    }
  };

  const isInDateRange = (dateStr: string) => {
    const now = new Date();
    const eventDate = new Date(dateStr);
    if (dateFilter === 'Today') {
      return eventDate.toDateString() === now.toDateString();
    }
    if (dateFilter === 'This Week') {
      const weekEnd = new Date(now);
      weekEnd.setDate(now.getDate() + 7);
      return eventDate >= now && eventDate <= weekEnd;
    }
    if (dateFilter === 'This Month') {
      return (
        eventDate.getMonth() === now.getMonth() &&
        eventDate.getFullYear() === now.getFullYear()
      );
    }
    return true;
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      searchQuery === '' ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.location_city?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'All' || event.category === selectedCategory;

    const matchesLocation =
      selectedLocation === 'All' ||
      event.location_city === selectedLocation ||
      event.location_country === selectedLocation;

    const matchesPrice =
      priceRange === 'All' ||
      (priceRange === 'Free' && (!event.price || event.price === 0)) ||
      (priceRange === 'Paid' && event.price && event.price > 0);

    const matchesDate = isInDateRange(event.start_date);

    return matchesSearch && matchesCategory && matchesLocation && matchesPrice && matchesDate;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
      case 'price-low':
        return (a.price || 0) - (b.price || 0);
      case 'price-high':
        return (b.price || 0) - (a.price || 0);
      case 'popular': {
        const aCount = a.registrations?.[0]?.count || 0;
        const bCount = b.registrations?.[0]?.count || 0;
        return bCount - aCount;
      }
      default:
        return 0;
    }
  });

  const categories = ['All', ...Array.from(new Set(events.map((e) => e.category)))];
  const locations = [
    'All',
    ...Array.from(
      new Set(events.map((e) => e.location_city || e.location_country).filter(Boolean))
    ),
  ] as string[];

  const hasActiveFilters =
    selectedCategory !== 'All' ||
    selectedLocation !== 'All' ||
    priceRange !== 'All' ||
    dateFilter !== 'All';

  const clearAllFilters = () => {
    setSelectedCategory('All');
    setSelectedLocation('All');
    setPriceRange('All');
    setDateFilter('All');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <EventsHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalEvents={events.length}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <EventsFilters
                categories={categories}
                locations={locations}
                selectedCategory={selectedCategory}
                selectedLocation={selectedLocation}
                priceRange={priceRange}
                dateFilter={dateFilter}
                onCategoryChange={setSelectedCategory}
                onLocationChange={setSelectedLocation}
                onPriceRangeChange={setPriceRange}
                onDateFilterChange={setDateFilter}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
              />

              {/* Create Event CTA */}
              <div className="mt-5 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-5 text-white">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-3">
                  <i className="ri-add-circle-line text-xl"></i>
                </div>
                <h4 className="font-bold text-sm mb-1">Host Your Own Event</h4>
                <p className="text-xs text-white/80 mb-3">
                  Reach thousands of attendees on EVENTSHOWCASE.
                </p>
                <button
                  onClick={() => navigate('/create-event')}
                  className="w-full py-2 bg-white text-teal-600 text-xs font-bold rounded-lg hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Create Event
                </button>
              </div>
            </div>
          </aside>

          {/* Events Area */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {loading ? 'Loading...' : `${sortedEvents.length} Events Found`}
                </h2>
                {hasActiveFilters && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Filters active —{' '}
                    <button
                      onClick={clearAllFilters}
                      className="text-teal-600 hover:underline cursor-pointer"
                    >
                      clear all
                    </button>
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* Sort */}
                <SortControls value={sortBy} onChange={setSortBy} />

                {/* View Toggle */}
                <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${
                      viewMode === 'grid'
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-grid-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${
                      viewMode === 'list'
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-list-check-2 text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Filters Row */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 mb-5 scrollbar-hide">
              {categories.slice(0, 8).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    selectedCategory === cat
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
              <button
                onClick={() => setPriceRange(priceRange === 'Free' ? 'All' : 'Free')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  priceRange === 'Free'
                    ? 'bg-teal-500 text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Free Only
              </button>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 text-sm">Loading events...</p>
              </div>
            ) : sortedEvents.length === 0 ? (
              <div className="text-center py-24">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-5">
                  <i className="ri-calendar-close-line text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No events found</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                  Try adjusting your filters or search query to find more events.
                </p>
                <button
                  onClick={clearAllFilters}
                  className="px-6 py-2.5 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Reset Filters
                </button>
              </div>
            ) : (
              <EventsGrid
                events={sortedEvents}
                isRegisteredMap={registeredEventIds}
                viewMode={viewMode}
              />
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
